import classNames from 'classnames';
import { TextBox } from 'devextreme-react';
import React, { useEffect, useReducer } from 'react';
import i18next from '../../../../assets/i18n/config';
import Button from '../../../../components/button/button';
import Dialog from '../../../../components/dialog/dialog';
import { IconEnum } from '../../../../components/icon';
import Icon from '../../../../components/icon/icon';
import Loading from '../../../../components/loading/loading';
import { CreateOneTimePasswordRequest, OneTimePasswordClient } from '../../../../services/partnerCenterService';
import { FREE_TRIAL_FAQ_URL } from '../../assets/urls/urls';
import './addTenantDialog.scss';

interface AddTenantDialogProps {
    closeDialog(): void;
    isOpen: boolean;
    onSuccess?: () => void;
}

const reducer = (state, action) => {
    switch (action.type) {
        case 'SET_TENANT_URL':
            return { ...state, tenantURL: action.payload };
        case 'SET_IS_URL_VALID':
            return { ...state, isURLValid: action.payload };
        case 'SET_IS_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_IS_SUCCESS':
            return { ...state, isSuccess: action.payload };
        case 'SET_IS_MESSAGE_VISIBLE':
            return { ...state, isMessageVisible: action.payload };
        case 'SET_CURRENT_STEP':
            return { ...state, currentStep: action.payload };
        case 'SET_ERROR_MESSAGE':
            return { ...state, errorMessage: action.payload };
        case 'SET_HAS_ERRORS':
            return { ...state, hasErrors: action.payload };
        case 'SET_INVITATION_LINK':
            return { ...state, invitationLink: action.payload };
        case 'SET_IS_COPY_SUCCESSFUL':
            return { ...state, isCopySuccessful: action.payload };
        default:
            return state;
    }
};

const initialState = {
    tenantURL: '',
    isURLValid: true,
    isLoading: false,
    isSuccess: false,
    isMessageVisible: false,
    currentStep: 0,
    errorMessage: '',
    hasErrors: false,
    invitationLink: '',
    isCopySuccessful: true
};

const AddTenantDialog: React.FC<AddTenantDialogProps> = props => {
    const { closeDialog, isOpen } = props;
    const [state, dispatch] = useReducer(reducer, initialState);

    const setTenantURL = (url: string) => dispatch({ type: 'SET_TENANT_URL', payload: url });
    const setIsURLValid = (urlValid: boolean) => dispatch({ type: 'SET_IS_URL_VALID', payload: urlValid });
    const setLoading = (loading: boolean) => dispatch({ type: 'SET_IS_LOADING', payload: loading });
    const setIsSuccess = (success: boolean) => dispatch({ type: 'SET_IS_SUCCESS', payload: success });
    const setIsMessageVisible = (messageVisible: boolean) => dispatch({ type: 'SET_IS_MESSAGE_VISIBLE', payload: messageVisible });
    const setCurrentStep = (step: number) => dispatch({ type: 'SET_CURRENT_STEP', payload: step });
    const setErrorMessage = (errorMessage: string) => dispatch({ type: 'SET_ERROR_MESSAGE', payload: errorMessage });
    const setHasErrors = (hasError: boolean) => dispatch({ type: 'SET_HAS_ERRORS', payload: hasError });
    const setInvitationLink = (invitationLink: string) => dispatch({ type: 'SET_INVITATION_LINK', payload: invitationLink });
    const setIsCopySuccessful = (copySuccess: boolean) => dispatch({ type: 'SET_IS_COPY_SUCCESSFUL', payload: copySuccess });
    const placeholderURL = i18next.t('partnerPortal.urlPlaceholder');

    useEffect(() => {
        setIsURLValid(true);
        setTenantURL('');
        setIsSuccess(false);
    }, [isOpen]);

    const isURLValid = url => {
        try {
            new URL(url);
            return true;
        } catch (err) {
            return false;
        }
    };
    //#region Actions

    const onURLValueChanged = e => {
        setIsURLValid(e.value && isURLValid(e.value));
        setIsURLValid(e.value != null);
        setTenantURL(e.value);
        setHasErrors(false);
    };

    const addTenant = async () => {
        setLoading(true);
        if (state.tenantURL) {
            try {
                const request: CreateOneTimePasswordRequest = {
                    url: state.tenantURL,
                };
                const oneTimePasswordRequest = new OneTimePasswordClient();
                const result = await oneTimePasswordRequest.create(request);
                if (result) {
                    setInvitationLink(result);
                    setIsSuccess(true);
                    setCurrentStep(state.currentStep + 1);
                }
                props.onSuccess?.();
            } catch (error) {
                console.error(error);
                setHasErrors(true);
                setErrorMessage(i18next.t('common:partnerPortal.errorMessage'));
            } finally {
                setLoading(false);
            }
        }
    };

    const handleCopyClick = () => {
        if (state.invitationLink)
            navigator.clipboard
                .writeText(state.invitationLink)
                .then(() => {
                    setIsMessageVisible(true);
                    setIsCopySuccessful(true);
                    setTimeout(() => {
                        setIsMessageVisible(false);
                    }, 1000);
                })
                .catch(err => {
                    setIsCopySuccessful(false);
                    setIsMessageVisible(true);
                    setTimeout(() => {
                        setIsCopySuccessful(false);
                        setIsMessageVisible(false);
                    }, 1000);
                });
    };

    const clearAllInfo = () => {
        closeDialog();
        setCurrentStep(0);
        setInvitationLink('');
        setIsURLValid(false);
        setIsSuccess(false);
    };

    //#endregion Actions

    const inputPointURLScreen = (
        <div>
            <div className="content_section">
                <p className="content_section__label">{i18next.t('common:partnerPortal.pleaseEnterUrl')}</p>

                <div className="url_section">
                    <div className="url_section__value">
                        <TextBox
                            placeholder={placeholderURL}
                            value={state.tenantURL}
                            valueChangeEvent="keyup"
                            onValueChanged={onURLValueChanged}
                            isValid={state.isURLValid}
                            defaultValue=""
                        ></TextBox>
                    </div>
                </div>
                {state.hasErrors && <div className='content_section__error'>{state.errorMessage}</div>}
            </div>

            <div className="next__button">
                <Button disabled={!state.tenantURL || !state.isURLValid || state.isSuccess} onClick={addTenant}>
                    {i18next.t('partnerPortal.addTenant')}
                </Button>
            </div>
        </div>
    );

    const copyLinkScreen = (
        <>
            <div className="content_section">
                <div className="content_section__label success">
                    {i18next.t('common:partnerPortal.yourTenantIsAdded')}
                </div>
                <div>{i18next.t('partnerPortal.signInWithGA')}</div>
                <a href={FREE_TRIAL_FAQ_URL} className="content_section__link">
                    {i18next.t('common:partnerPortal.learnMore')} <Icon iconName={IconEnum.ExternalLink} />
                </a>

                <div className="tenant_added">
                    <TextBox disabled={true} value={state.invitationLink}></TextBox>
                    <Button onClick={handleCopyClick} className="outline">
                        <Icon iconName={IconEnum.Copy}></Icon> {i18next.t('common:partnerPortal.copyInvitationLink')}
                    </Button>
                </div>
                {!state.isCopySuccessful && state.isMessageVisible && (
                    <div className="content_section__error">{i18next.t('common:partnerPortal.copyErrorMessage')}</div>
                )}
            </div>

            <div
                className={classNames('copied_message', { visible: state.isMessageVisible && state.isCopySuccessful })}
            >
                <Icon iconName={IconEnum.Check}></Icon>
                <p>{i18next.t('common:partnerPortal.copiedToClipboard')}</p>
            </div>
            <div className="next__button">
                <Button onClick={clearAllInfo}>{i18next.t('partnerPortal.close')}</Button>
            </div>
        </>
    );

    const screens = Array.from([inputPointURLScreen, copyLinkScreen]);
    //#region Rendering

    const renderFooter = () => {
        return (
            <div className="multi_step__dialog-footer">
                <div className="progress_dots">
                    {screens.map((_, index) => (
                        <div
                            key={index}
                            className={classNames('progress_dots__dot', { current: index === state.currentStep })}
                        ></div>
                    ))}
                </div>
            </div>
        );
    };

    //#endregion Rendering

    return (
        <div>
            <Dialog
                title={i18next.t('partnerPortal.addTenant')}
                isOpen={isOpen}
                onClose={clearAllInfo}
                footer={renderFooter()}
                className="add_tenant__dialog"
            >
                {state.isLoading && (
                    <div className="invite_user__loading">
                        <Loading />
                    </div>
                )}
                {!state.isLoading && screens[state.currentStep]}
            </Dialog>
        </div>
    );
};

export default AddTenantDialog;
